import { useEffect, useState } from 'react';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
  useIsAuthenticated,
} from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, apiScopes } from './msalConfig';
import { isAuthEnabled } from './authEnabled';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
}

/**
 * Wrapper that requires the user to be authenticated via Entra ID.
 *
 * When auth is not configured (no ENTRA_CLIENT_ID), children are
 * rendered directly without any login requirement.
 *
 * In production, unauthenticated users are automatically redirected
 * to the Entra ID login page. Role checks use the access token's
 * roles claim (not the ID token, which doesn't include app roles).
 */
export default function ProtectedRoute({ children, roles }: ProtectedRouteProps) {
  if (!isAuthEnabled) {
    return <>{children}</>;
  }

  return <EntraProtectedRoute roles={roles}>{children}</EntraProtectedRoute>;
}

function EntraProtectedRoute({ children, roles }: ProtectedRouteProps) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  // Auto-redirect to login when not authenticated and no interaction in progress
  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      instance.loginRedirect(loginRequest);
    }
  }, [isAuthenticated, inProgress, instance]);

  // Extract roles from the access token (not the ID token, which lacks app roles)
  useEffect(() => {
    if (!isAuthenticated || inProgress !== InteractionStatus.None) return;

    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) return;

    instance.acquireTokenSilent({ ...apiScopes, account })
      .then((response) => {
        // Parse the access token payload to extract roles
        try {
          const payload = response.accessToken.split('.')[1];
          const decoded = JSON.parse(atob(payload));
          setUserRoles(decoded.roles ?? []);
        } catch {
          setUserRoles([]);
        }
        setRolesLoaded(true);
      })
      .catch(() => {
        setRolesLoaded(true);
      });
  }, [isAuthenticated, inProgress, instance, accounts]);

  const hasRequiredRole = () => {
    if (!roles || roles.length === 0) return true;
    if (userRoles.includes('ADMIN')) return true;
    return roles.some((role) => userRoles.includes(role));
  };

  return (
    <>
      <AuthenticatedTemplate>
        {!rolesLoaded ? (
          <main id="main-content" className="flex items-center justify-center min-h-[60vh]">
            <p className="text-gray-500">Loading...</p>
          </main>
        ) : hasRequiredRole() ? (
          children
        ) : (
          <main
            id="main-content"
            className="flex flex-col items-center justify-center min-h-[60vh] gap-4"
          >
            <h1 className="text-2xl font-bold text-gray-800">Access Denied</h1>
            <p className="text-gray-500">
              You do not have the required permissions to view this page.
            </p>
          </main>
        )}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <main
          id="main-content"
          className="flex items-center justify-center min-h-[60vh]"
        >
          <p className="text-gray-500">Redirecting to sign in...</p>
        </main>
      </UnauthenticatedTemplate>
    </>
  );
}
